import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { formatCreatedAtDate, getCategoriesNameById, formatNumberWithComma } from '../../utils/helpers';

// Helper function to get servant name
const getServantName = (servantId, employeeNames) => {
    if (!servantId || !employeeNames || !Array.isArray(employeeNames)) return 'N/A';
    const employee = employeeNames.find(emp => emp.id === servantId);
    return employee ? (employee.employee_name || employee.name) : 'N/A';
};

// Helper function to calculate item totals and details
const getOrderItemsSummary = (order) => {
    const items = order.club_order_items || [];
    const totalItems = items.length;
    const uniqueServants = [...new Set(items.map(item => item.servant_id))];
    
    return {
        totalItems,
        uniqueServants: uniqueServants.length,
        items
    };
};

// Helper function for CSV export
export const exportOrdersToCSV = (data, employeeNames = []) => {
    const headers = [
        'Order Number', 
        'Date', 
        'Time',
        'Customer ID', 
        'Customer Type',
        'Servant(s)', 
        'Counter', 
        'Items Count',
        'Item Details',
        'Total Amount', 
        'Status', 
        'Payment Method',
        'Transaction ID',
        'Order Notes',
        'Payment Notes'
    ];

    const csvData = data.map(order => {
        const items = order.club_order_items || [];
        const itemCount = items.length;
        const servantIds = [...new Set(items.map(item => item.servant_id))];
        const servantNames = servantIds.map(id => getServantName(id, employeeNames)).join('; ');
        
        // Create item details string
        const itemDetails = items.map(item => 
            `${item.stock_name} (${item.quantity} x ${formatNumberWithComma(item.price)} = ${formatNumberWithComma(item.total_amount)})`
        ).join(' | ');
        
        return [
            order.order_number,
            new Date(order.created_at).toLocaleDateString(),
            new Date(order.created_at).toLocaleTimeString(),
            order.customer_id,
            order.customer_type?.name || 'N/A',
            servantNames || 'N/A',
            order.counter?.name || 'N/A',
            itemCount,
            itemDetails,
            order.total_amount,
            order.order_status.toUpperCase(),
            order.payment_method.toUpperCase(),
            order.transaction_id || '',
            order.order_notes || '',
            order.payment_notes || ''
        ];
    });

    const csvContent = [
        headers.join(','),
        ...csvData.map(row => {
            // Escape commas and quotes in fields
            return row.map(cell => {
                if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
                    return `"${cell.replace(/"/g, '""')}"`;
                }
                return cell;
            }).join(',');
        })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `club_orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
};

// Helper function for Excel export
export const exportOrdersToExcel = async (data, employeeNames = []) => {
    // Create main orders sheet
    const ordersSheetData = [
        [
            'Order Number', 
            'Date', 
            'Time',
            'Customer ID', 
            'Customer Type',
            'Servant(s)', 
            'Counter', 
            'Items Count',
            'Total Amount', 
            'Status', 
            'Payment Method',
            'Transaction ID',
            'Order Notes',
            'Payment Notes',
            'Created At'
        ],
        ...data.map(order => {
            const items = order.club_order_items || [];
            const itemCount = items.length;
            const servantIds = [...new Set(items.map(item => item.servant_id))];
            const servantNames = servantIds.map(id => getServantName(id, employeeNames)).join(', ');
            
            return [
                order.order_number,
                new Date(order.created_at).toLocaleDateString(),
                new Date(order.created_at).toLocaleTimeString(),
                order.customer_id,
                order.customer_type?.name || 'N/A',
                servantNames || 'N/A',
                order.counter?.name || 'N/A',
                itemCount,
                order.total_amount,
                order.order_status.toUpperCase(),
                order.payment_method.toUpperCase(),
                order.transaction_id || '',
                order.order_notes || '',
                order.payment_notes || '',
                new Date(order.created_at).toLocaleString()
            ];
        })
    ];

    // Create items sheet with detailed item information
    const itemsSheetData = [
        [
            'Order Number',
            'Date',
            'Time',
            'Customer ID',
            'Item Name',
            'Quantity',
            'Price',
            'Total',
            'Servant',
            'Item Notes'
        ]
    ];

    data.forEach(order => {
        const items = order.club_order_items || [];
        items.forEach(item => {
            itemsSheetData.push([
                order.order_number,
                new Date(order.created_at).toLocaleDateString(),
                new Date(item.time).toLocaleTimeString(),
                order.customer_id,
                item.stock_name,
                item.quantity,
                item.price,
                item.total_amount,
                getServantName(item.servant_id, employeeNames),
                item.order_notes || ''
            ]);
        });
    });

    const workbook = XLSX.utils.book_new();
    
    // Add Orders sheet
    const ordersWorksheet = XLSX.utils.aoa_to_sheet(ordersSheetData);
    const ordersWscols = [
        { wch: 20 }, // Order Number
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 20 }, // Customer ID
        { wch: 15 }, // Customer Type
        { wch: 25 }, // Servant(s)
        { wch: 15 }, // Counter
        { wch: 12 }, // Items Count
        { wch: 15 }, // Total Amount
        { wch: 12 }, // Status
        { wch: 15 }, // Payment Method
        { wch: 20 }, // Transaction ID
        { wch: 30 }, // Order Notes
        { wch: 30 }, // Payment Notes
        { wch: 20 }, // Created At
    ];
    ordersWorksheet['!cols'] = ordersWscols;
    XLSX.utils.book_append_sheet(workbook, ordersWorksheet, 'Orders');

    // Add Items sheet
    const itemsWorksheet = XLSX.utils.aoa_to_sheet(itemsSheetData);
    const itemsWscols = [
        { wch: 20 }, // Order Number
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 20 }, // Customer ID
        { wch: 25 }, // Item Name
        { wch: 10 }, // Quantity
        { wch: 15 }, // Price
        { wch: 15 }, // Total
        { wch: 20 }, // Servant
        { wch: 30 }, // Item Notes
    ];
    itemsWorksheet['!cols'] = itemsWscols;
    XLSX.utils.book_append_sheet(workbook, itemsWorksheet, 'Order Items');

    XLSX.writeFile(workbook, `club_orders_${new Date().toISOString().split('T')[0]}.xlsx`);
};

export const exportOrdersToPDFEnhanced = (data, employeeNames = [], filters = {}) => {
    try {
        const doc = new jsPDF('landscape');
        
        // Set document properties
        doc.setProperties({
            title: 'Club Orders Report',
            subject: 'Orders Export',
            author: 'Club Management System',
            keywords: 'orders, club, sales, export, report',
            creator: 'Club Management System'
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('CLUB ORDERS REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
        
        // System name
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        doc.text('Club Management System', doc.internal.pageSize.width / 2, 35, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 45;
        
        const leftColumnX = 14;
        const rightColumnX = doc.internal.pageSize.width / 2 + 20;
        
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
        doc.text(`Total Orders: ${data.length}`, rightColumnX, currentY);
        currentY += 8;
        
        // Calculate totals
        if (data.length > 0) {
            const totalAmount = data.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
            const pendingOrders = data.filter(order => order.order_status === 'pending').length;
            const completedOrders = data.filter(order => order.order_status === 'completed').length;
            const totalItems = data.reduce((sum, order) => sum + (order.club_order_items?.length || 0), 0);
            
            doc.text(`Total Amount: ${formatNumberWithComma(totalAmount)}`, leftColumnX, currentY);
            doc.text(`Pending Orders: ${pendingOrders}`, rightColumnX, currentY);
            currentY += 8;
            doc.text(`Completed Orders: ${completedOrders}`, leftColumnX, currentY);
            doc.text(`Total Items Sold: ${totalItems}`, rightColumnX, currentY);
            currentY += 8;
        }
        
        // Filters applied
        if (Object.values(filters).some(v => v)) {
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.text('Applied Filters:', leftColumnX, currentY);
            currentY += 6;
            
            let filterLines = [];
            if (filters.order_status) filterLines.push(`• Status: ${filters.order_status}`);
            if (filters.payment_method) filterLines.push(`• Payment Method: ${filters.payment_method}`);
            if (filters.dateFrom) filterLines.push(`• From date: ${filters.dateFrom}`);
            if (filters.dateTo) filterLines.push(`• To date: ${filters.dateTo}`);
            if (filters.order_number) filterLines.push(`• Order #: ${filters.order_number}`);
            
            // Display filters in two columns
            const midPoint = Math.ceil(filterLines.length / 2);
            const leftFilters = filterLines.slice(0, midPoint);
            const rightFilters = filterLines.slice(midPoint);
            
            const maxLines = Math.max(leftFilters.length, rightFilters.length);
            
            for (let i = 0; i < maxLines; i++) {
                if (leftFilters[i]) {
                    doc.text(leftFilters[i], leftColumnX + 5, currentY);
                }
                if (rightFilters[i]) {
                    doc.text(rightFilters[i], rightColumnX, currentY);
                }
                currentY += 6;
            }
            currentY += 6;
        }
        
        // Prepare table data
        const tableData = data.map((order, index) => {
            const items = order.club_order_items || [];
            const servantIds = [...new Set(items.map(item => item.servant_id))];
            const servantNames = servantIds.map(id => getServantName(id, employeeNames)).join(', ');
            
            return {
                no: index + 1,
                date: new Date(order.created_at).toLocaleDateString(),
                orderNumber: order.order_number || '',
                customer: order.customer_id,
                servant: servantNames.length > 20 ? servantNames.substring(0, 20) + '...' : servantNames || 'N/A',
                items: items.length.toString(),
                total: formatNumberWithComma(order.total_amount || 0),
                status: order.order_status?.toUpperCase() || 'N/A',
                payment: order.payment_method?.toUpperCase() || 'N/A'
            };
        });
        
        // Draw the table
        currentY = drawOrdersTable(doc, tableData, currentY + 10);
        
        // Add footer with page number
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
            doc.text(
                `Generated on: ${new Date().toLocaleDateString()}`,
                doc.internal.pageSize.width - 14,
                doc.internal.pageSize.height - 10,
                { align: 'right' }
            );
        }
        
        const fileName = `club_orders_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Orders PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

const drawOrdersTable = (doc, tableData, startY) => {
    // Table header
    const tableTop = startY;
    const marginLeft = 10;
    const marginRight = 10;
    const pageWidth = doc.internal.pageSize.width;
    const tableWidth = pageWidth - marginLeft - marginRight;
    
    // Calculate column widths
    const colWidths = {
        no: 12,
        date: 25,
        orderNumber: 35,
        customer: 30,
        servant: 40,
        items: 15,
        total: 30,
        status: 25,
        payment: 25
    };
    
    // Check if total width fits page
    const totalWidth = Object.values(colWidths).reduce((sum, width) => sum + width, 0);
    
    // If total width exceeds available width, adjust proportionally
    if (totalWidth > tableWidth) {
        const scaleFactor = tableWidth / totalWidth;
        Object.keys(colWidths).forEach(key => {
            colWidths[key] = Math.floor(colWidths[key] * scaleFactor);
        });
    }
    
    // Draw table header
    let currentX = marginLeft;
    let currentY = tableTop;
    
    // Header background
    doc.setFillColor(41, 128, 185);
    doc.rect(currentX, currentY, tableWidth, 10, 'F');
    
    // Header text
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    // Draw header cells
    const headers = [
        { text: '#', width: colWidths.no },
        { text: 'Date', width: colWidths.date },
        { text: 'Order #', width: colWidths.orderNumber },
        { text: 'Customer', width: colWidths.customer },
        { text: 'Servant(s)', width: colWidths.servant },
        { text: 'Items', width: colWidths.items },
        { text: 'Total', width: colWidths.total },
        { text: 'Status', width: colWidths.status },
        { text: 'Payment', width: colWidths.payment }
    ];
    
    currentX = marginLeft + 2;
    headers.forEach(header => {
        doc.text(header.text, currentX, currentY + 7);
        currentX += header.width;
    });
    
    // Draw rows
    currentY += 12;
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    
    let rowIndex = 0;
    const rowHeight = 8;
    const pageHeight = doc.internal.pageSize.height;
    
    for (const row of tableData) {
        // Check if we need a new page
        if (currentY > pageHeight - 20) {
            doc.addPage('landscape');
            currentY = 20;
            
            // Redraw header on new page
            doc.setFillColor(41, 128, 185);
            doc.rect(marginLeft, currentY, tableWidth, 10, 'F');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            currentX = marginLeft + 2;
            headers.forEach(header => {
                doc.text(header.text, currentX, currentY + 7);
                currentX += header.width;
            });
            
            currentY += 12;
        }
        
        // Alternate row background
        if (rowIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
            doc.rect(marginLeft, currentY - 2, tableWidth, rowHeight + 2, 'F');
        }
        
        // Draw cell borders (vertical lines)
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        
        currentX = marginLeft;
        headers.forEach(header => {
            doc.line(currentX, currentY - 2, currentX, currentY + rowHeight);
            currentX += header.width;
        });
        doc.line(currentX, currentY - 2, currentX, currentY + rowHeight); // Last vertical line
        
        // Draw horizontal line
        doc.line(marginLeft, currentY + rowHeight, marginLeft + tableWidth, currentY + rowHeight);
        
        // Draw row data
        currentX = marginLeft + 2;
        
        // Row number
        doc.text(row.no.toString(), currentX, currentY + 6);
        currentX += colWidths.no;
        
        // Date
        doc.text(row.date, currentX + 2, currentY + 6);
        currentX += colWidths.date;
        
        // Order Number
        doc.text(row.orderNumber, currentX + 2, currentY + 6);
        currentX += colWidths.orderNumber;
        
        // Customer
        doc.text(row.customer.length > 15 ? row.customer.substring(0, 15) + '...' : row.customer, currentX + 2, currentY + 6);
        currentX += colWidths.customer;
        
        // Servant
        doc.text(row.servant, currentX + 2, currentY + 6);
        currentX += colWidths.servant;
        
        // Items
        doc.text(row.items, currentX + 2, currentY + 6);
        currentX += colWidths.items;
        
        // Total Amount
        doc.text(row.total, currentX + 2, currentY + 6);
        currentX += colWidths.total;
        
        // Status - Color code
        if (row.status === 'COMPLETED') {
            doc.setTextColor(0, 150, 0);
        } else if (row.status === 'PENDING') {
            doc.setTextColor(255, 165, 0);
        } else if (row.status === 'CANCELLED') {
            doc.setTextColor(255, 0, 0);
        }
        doc.text(row.status, currentX + 2, currentY + 6);
        doc.setTextColor(0, 0, 0);
        currentX += colWidths.status;
        
        // Payment - Color code
        if (row.payment === 'CASH') {
            doc.setTextColor(0, 100, 0);
        } else if (row.payment === 'CARD' || row.payment === 'BANK') {
            doc.setTextColor(0, 0, 150);
        } else if (row.payment === 'PENDING') {
            doc.setTextColor(255, 165, 0);
        }
        doc.text(row.payment, currentX + 2, currentY + 6);
        doc.setTextColor(0, 0, 0);
        
        currentY += rowHeight + 2;
        rowIndex++;
    }
    
    // Draw top border of table
    doc.line(marginLeft, tableTop - 2, marginLeft + tableWidth, tableTop - 2);
    
    return currentY + 10;
};